import { BaseForm } from '@app/src/common/components/form/BaseForm';
import {
  ConditionType,
  DependenceRule,
  DependenceType,
  IDependence,
  IFormField,
  IValidator
} from '@app/src/common/model/FormField';
import { FormValidator } from '@app/src/common/model/FormValidator';
import {
  addJSONValidator,
  addLengthIsNotMoreThan,
  addMoreThanValidator,
  addRequiredValidator,
  addSameDateValidator,
  addSkipCheckValidator,
  addTimeRangeValidator,
  addValidExpressionValidator,
  addValueIsNotLessThan,
  addValueIsNotMoreThan
} from '@app/src/common/components/form/Validators';

export function addValidator(
  form: BaseForm,
  field: IFormField,
  validator: IValidator
) {
  switch (validator.type) {
    case FormValidator.LENGTH_LIMIT:
      addLengthIsNotMoreThan(form, field.name, validator.value as number);
      break;
    case FormValidator.MORE_THAN:
      addMoreThanValidator(
        form,
        field.name,
        validator.value as string,
        validator.message
      );
      break;
    case FormValidator.SAME_DATE:
      addSameDateValidator(
        form,
        field.name,
        validator.value as string,
        validator.message
      );
      break;
    case FormValidator.TIME_RANGE:
      addTimeRangeValidator(
        form,
        field.name,
        validator.props?.start,
        validator.props?.end,
        validator.message
      );
      break;
    case FormValidator.NOT_LESS_THAN:
      addValueIsNotLessThan(
        form,
        field.name,
        validator.value as number,
        validator.message
      );
      break;
    case FormValidator.NOT_MORE_THAN:
      addValueIsNotMoreThan(
        form,
        field.name,
        validator.value as number,
        validator.message
      );
      break;
    case FormValidator.REQUIRED:
      applyRequiredValidator(form, field, validator);
      break;
    case FormValidator.EXPRESSION: {
      const regEx = toRegExp(validator.value);
      if (regEx) {
        addValidExpressionValidator(form, field.name, regEx, validator.message);
      }
      break;
    }
    case FormValidator.JSON:
      addJSONValidator(form, field.name, validator.message);
      break;
  }
}

function applyRequiredValidator(
  form: BaseForm,
  field: IFormField,
  validator: IValidator
) {
  addRequiredValidator(form, field.name, validator.message);
  const { dependOn } = field;
  if (!dependOn) {
    return;
  }
  if (dependOn.type === DependenceType.REQUIRED) {
    addSkipCheckValidator(
      form as any,
      field.name,
      buildRequiredSkipCheck(dependOn)
    );
  } else if (dependOn.type === DependenceType.HIDE) {
    addSkipCheckValidator(
      form as any,
      field.name,
      buildHideSkipCheck(dependOn)
    );
  }
}

function buildRequiredSkipCheck(dependOn: IDependence) {
  const { field: fieldName, value, rule = DependenceRule.EQUAL } = dependOn;
  if (rule === DependenceRule.NOT_EQUAL) {
    return (target: any) => target[fieldName].value !== value;
  }
  return (target: any) => target[fieldName].value === value;
}

function buildHideSkipCheck(dependOn: IDependence) {
  const {
    field: fieldName,
    value,
    rule = DependenceRule.EQUAL,
    condition
  } = dependOn;
  if (condition === ConditionType.EMPTY) {
    return (target: any) => !target[fieldName]?.value;
  }
  if (rule === DependenceRule.NOT_EQUAL) {
    return (target: any) => target[fieldName]?.value !== value;
  }
  return (target: any) => target[fieldName]?.value === value;
}

// A pattern may be given as a RegExp or as its source string, so a field config can stay plain
// serializable data (see fw-truck-mgm DymanicForm). Note `regEx.test` is not anchored for you -
// a pattern meant to describe the *whole* value must say so with ^...$.
function toRegExp(value: IValidator['value']): RegExp | undefined {
  if (value instanceof RegExp) {
    return value;
  }
  return value ? new RegExp(String(value)) : undefined;
}
